import React, { useEffect, useMemo, useState } from "react";
import { collection, addDoc, getDocs, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { OpsModal, ConfirmDialog, Pill, Field, EmptyState } from "./OpsUi";

// ---------------------------------------------------------------------------
// invoiceDropdowns data shape (unchanged — read by JobCompletionInvoiceModal,
// InvoicePPD and PPDSentInvoices):
//   { label, fieldName, order, options: [{ title, value, subOptions?, category?, ...}] }
// The invoice forms display option.title ?? option.label ?? option.value and
// group by option.category when any option has one.
// ---------------------------------------------------------------------------

const REQUIRED_DROPDOWNS = [
  { label: "Dropdown", fieldName: "dropdown" },
  { label: "Chemical Used", fieldName: "chemicalUsed" },
  { label: "Pest Treated", fieldName: "pestTreated" },
  { label: "Areas Treated", fieldName: "areasTreated" },
  { label: "Wind Speed", fieldName: "windSpeed" },
  { label: "Equipment Used", fieldName: "equipmentUsed" },
  { label: "Weather", fieldName: "weather" },
  { label: "Temperature", fieldName: "temperature" },
  { label: "Wind Direction", fieldName: "windDirection" },
];
const REQUIRED_FIELDS = REQUIRED_DROPDOWNS.map((d) => d.fieldName);

const FIELD_HELP = {
  dropdown: "General selections added to the job description.",
  chemicalUsed: "Products applied — technicians enter a quantity for each one.",
  pestTreated: "Pests targeted on the service.",
  areasTreated: "Interior / exterior areas serviced.",
  windSpeed: "Wind speed at time of application.",
  equipmentUsed: "Sprayers, dusters and other application equipment.",
  weather: "Conditions at time of application.",
  temperature: "Temperature at time of application.",
  windDirection: "Wind direction at time of application.",
};

const optionText = (opt) => {
  if (opt === null || opt === undefined) return "";
  if (typeof opt !== "object") return String(opt);
  return String(opt.title ?? opt.label ?? opt.value ?? "");
};

const toObjectOption = (opt) =>
  opt && typeof opt === "object" ? opt : { title: String(opt ?? ""), value: String(opt ?? ""), subOptions: [] };

const camelCase = (str) =>
  String(str || "")
    .trim()
    .replace(/[^a-zA-Z0-9 ]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w, i) => (i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1)))
    .join("");

const EMPTY_OPTION_DRAFT = { title: "", value: "", category: "", subOptions: [] };

const InvoiceDropdownsAdmin = () => {
  const [dropdowns, setDropdowns] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const [listSearch, setListSearch] = useState("");
  const [optionSearch, setOptionSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [newDropdown, setNewDropdown] = useState(null); // { label, fieldName, touched }
  const [editDropdown, setEditDropdown] = useState(null); // { id, label, fieldName }
  const [confirmDelete, setConfirmDelete] = useState(null); // dropdown
  const [optionEditor, setOptionEditor] = useState(null); // { mode, index, data, newSub }
  const [optionToDelete, setOptionToDelete] = useState(null); // index
  const [bulkAdd, setBulkAdd] = useState(null); // { text, category }
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [bulkSubOptionText, setBulkSubOptionText] = useState("");

  const [termEditor, setTermEditor] = useState(null); // { id?, name, content }
  const [termToDelete, setTermToDelete] = useState(null);

  const [busy, setBusy] = useState(false);

  // ------------------------------------------------------------------ load
  const fetchTerms = async () => {
    const snap = await getDocs(collection(db, "invoiceTerms"));
    setTerms(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchDropdowns = async () => {
    const snap = await getDocs(collection(db, "invoiceDropdowns"));
    const data = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
    setDropdowns(data);
    return data;
  };

  const ensureRequiredDropdowns = async () => {
    const snap = await getDocs(collection(db, "invoiceDropdowns"));
    const existing = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    for (let i = 0; i < REQUIRED_DROPDOWNS.length; i++) {
      const req = REQUIRED_DROPDOWNS[i];
      if (!existing.find((d) => d.fieldName === req.fieldName)) {
        await addDoc(collection(db, "invoiceDropdowns"), {
          label: req.label,
          fieldName: req.fieldName,
          options: [],
          order: existing.length + i + 1,
        });
      }
    }
  };

  useEffect(() => {
    fetchTerms().catch((e) => console.error("Could not load invoice terms:", e));
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        await ensureRequiredDropdowns();
        const data = await fetchDropdowns();
        setSelectedId((prev) => prev || data[0]?.id || null);
      } catch (e) {
        console.error("Could not load invoice dropdowns:", e);
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = dropdowns.find((d) => d.id === selectedId) || null;
  const selectedOptions = useMemo(() => (Array.isArray(selected?.options) ? selected.options : []), [selected]);

  useEffect(() => {
    setOptionSearch("");
    setCategoryFilter("");
    setShowAdvanced(false);
  }, [selectedId]);

  // ------------------------------------------------------------------ writes
  const saveOptions = async (dropdownId, options) => {
    await updateDoc(doc(db, "invoiceDropdowns", dropdownId), { options });
    setDropdowns((prev) => prev.map((d) => (d.id === dropdownId ? { ...d, options } : d)));
  };

  const run = async (fn, errorMessage = "Something went wrong saving your change.") => {
    setBusy(true);
    try {
      await fn();
      return true;
    } catch (e) {
      console.error(e);
      alert(errorMessage);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // dropdown CRUD
  const addDropdown = async () => {
    const label = String(newDropdown?.label || "").trim();
    const fieldName = String(newDropdown?.fieldName || "").trim();
    if (!label || !fieldName) {
      alert("Please enter a label and a field name.");
      return;
    }
    if (dropdowns.some((d) => d.fieldName === fieldName)) {
      alert(`A dropdown with field name "${fieldName}" already exists.`);
      return;
    }
    await run(async () => {
      const maxOrder = dropdowns.reduce((m, d) => Math.max(m, Number(d.order || 0)), 0);
      const ref = await addDoc(collection(db, "invoiceDropdowns"), {
        label,
        fieldName,
        options: [],
        order: Math.max(maxOrder, dropdowns.length) + 1,
      });
      await fetchDropdowns();
      setSelectedId(ref.id);
      setNewDropdown(null);
    });
  };

  const saveEditedDropdown = async () => {
    const label = String(editDropdown?.label || "").trim();
    const fieldName = String(editDropdown?.fieldName || "").trim();
    if (!label || !fieldName) return;
    if (dropdowns.some((d) => d.id !== editDropdown.id && d.fieldName === fieldName)) {
      alert(`A dropdown with field name "${fieldName}" already exists.`);
      return;
    }
    await run(async () => {
      await updateDoc(doc(db, "invoiceDropdowns", editDropdown.id), { label, fieldName });
      setDropdowns((prev) => prev.map((d) => (d.id === editDropdown.id ? { ...d, label, fieldName } : d)));
      setEditDropdown(null);
    });
  };

  const deleteDropdown = async () => {
    if (!confirmDelete) return;
    await run(async () => {
      await deleteDoc(doc(db, "invoiceDropdowns", confirmDelete.id));
      const remaining = dropdowns.filter((d) => d.id !== confirmDelete.id);
      setDropdowns(remaining);
      setSelectedId(remaining[0]?.id || null);
      setConfirmDelete(null);
    });
  };

  const moveDropdown = async (id, dir) => {
    const idx = dropdowns.findIndex((d) => d.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= dropdowns.length) return;
    const next = [...dropdowns];
    [next[idx], next[target]] = [next[target], next[idx]];
    const renumbered = next.map((d, i) => ({ ...d, order: i + 1 }));
    setDropdowns(renumbered);
    await run(async () => {
      await Promise.all(
        renumbered
          .filter((d) => {
            const before = dropdowns.find((x) => x.id === d.id);
            return Number(before?.order) !== d.order;
          })
          .map((d) => updateDoc(doc(db, "invoiceDropdowns", d.id), { order: d.order }))
      );
    }, "Could not save the new order.");
  };

  // option CRUD
  const openAddOption = () =>
    setOptionEditor({ mode: "add", index: null, data: { ...EMPTY_OPTION_DRAFT, category: categoryFilter || "" }, newSub: "" });

  const openEditOption = (index) => {
    const opt = toObjectOption(selectedOptions[index]);
    setOptionEditor({
      mode: "edit",
      index,
      data: {
        title: optionText(opt),
        value: opt.value !== undefined && String(opt.value) !== optionText(opt) ? String(opt.value) : "",
        category: opt.category || "",
        subOptions: Array.isArray(opt.subOptions) ? [...opt.subOptions] : [],
      },
      newSub: "",
    });
  };

  const saveOption = async () => {
    if (!selected || !optionEditor) return;
    const title = optionEditor.data.title.trim();
    if (!title) {
      alert("Please enter an option title.");
      return;
    }
    const paragraph = optionEditor.data.value.trim();
    const category = optionEditor.data.category.trim();
    const subOptions = optionEditor.data.subOptions.map((s) => String(s).trim()).filter(Boolean);

    const options = [...selectedOptions];
    if (optionEditor.mode === "add") {
      const newOption = { title, value: paragraph || title, subOptions };
      if (category) newOption.category = category;
      options.push(newOption);
    } else {
      const original = toObjectOption(options[optionEditor.index]);
      const oldTitle = optionText(original);
      const valueTrackedTitle = original.value === undefined || String(original.value) === oldTitle;
      const updated = {
        ...original,
        title,
        value: paragraph || (valueTrackedTitle ? title : original.value),
        subOptions,
      };
      if (category) updated.category = category;
      else delete updated.category;
      // keep a legacy `label` key in sync if the option used one
      if (updated.label !== undefined) updated.label = title;
      options[optionEditor.index] = updated;
    }
    const ok = await run(() => saveOptions(selected.id, options));
    if (ok) setOptionEditor(null);
  };

  const deleteOption = async () => {
    if (!selected || optionToDelete === null) return;
    const options = selectedOptions.filter((_, i) => i !== optionToDelete);
    const ok = await run(() => saveOptions(selected.id, options));
    if (ok) setOptionToDelete(null);
  };

  const hasCategories = selectedOptions.some((o) => o && typeof o === "object" && o.category);

  const moveOption = async (index, dir) => {
    if (!selected) return;
    const cat = (o) => (o && typeof o === "object" ? o.category || "" : "");
    const current = selectedOptions[index];
    let target = index + dir;
    // when grouped by category, swap with the neighbour in the same group
    while (hasCategories && target >= 0 && target < selectedOptions.length && cat(selectedOptions[target]) !== cat(current)) {
      target += dir;
    }
    if (target < 0 || target >= selectedOptions.length) return;
    const options = [...selectedOptions];
    [options[index], options[target]] = [options[target], options[index]];
    await run(() => saveOptions(selected.id, options), "Could not reorder.");
  };

  const sortOptionsAZ = async () => {
    if (!selected) return;
    const options = [...selectedOptions].sort((a, b) => {
      const ca = (a && a.category) || "";
      const cb = (b && b.category) || "";
      if (ca !== cb) return ca.localeCompare(cb);
      return optionText(a).localeCompare(optionText(b), undefined, { numeric: true, sensitivity: "base" });
    });
    await run(() => saveOptions(selected.id, options), "Could not sort.");
  };

  const addBulkOptions = async () => {
    if (!selected || !bulkAdd) return;
    const items = bulkAdd.text
      .split(/[;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!items.length) {
      alert("Enter at least one option.");
      return;
    }
    const existing = new Set(selectedOptions.map((o) => optionText(o).toLowerCase()));
    const category = bulkAdd.category.trim();
    const fresh = items
      .filter((t, i) => items.findIndex((x) => x.toLowerCase() === t.toLowerCase()) === i)
      .filter((t) => !existing.has(t.toLowerCase()))
      .map((t) => {
        const o = { title: t, value: t, subOptions: [] };
        if (category) o.category = category;
        return o;
      });
    if (!fresh.length) {
      alert("All of those options already exist.");
      return;
    }
    const ok = await run(() => saveOptions(selected.id, [...selectedOptions, ...fresh]));
    if (ok) setBulkAdd(null);
  };

  const addBulkSubOptionParagraph = async () => {
    if (!selected) return;
    const paragraph = bulkSubOptionText.trim();
    if (!paragraph) return;
    const options = selectedOptions.map((opt) => {
      const o = toObjectOption(opt);
      return { ...o, subOptions: [...(o.subOptions || []), paragraph] };
    });
    const ok = await run(() => saveOptions(selected.id, options));
    if (ok) setBulkSubOptionText("");
  };

  // terms CRUD
  const saveTerm = async () => {
    const name = String(termEditor?.name || "").trim();
    const content = String(termEditor?.content || "");
    if (!name) {
      alert("Please enter a term name.");
      return;
    }
    const ok = await run(async () => {
      if (termEditor.id) {
        await updateDoc(doc(db, "invoiceTerms", termEditor.id), { name, content });
      } else {
        await addDoc(collection(db, "invoiceTerms"), { name, content });
      }
      await fetchTerms();
    });
    if (ok) setTermEditor(null);
  };

  const deleteTerm = async () => {
    if (!termToDelete) return;
    const ok = await run(async () => {
      await deleteDoc(doc(db, "invoiceTerms", termToDelete.id));
      await fetchTerms();
    });
    if (ok) setTermToDelete(null);
  };

  // ------------------------------------------------------------------ derived
  const visibleDropdowns = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    if (!q) return dropdowns;
    return dropdowns.filter(
      (d) =>
        String(d.label || "").toLowerCase().includes(q) ||
        String(d.fieldName || "").toLowerCase().includes(q) ||
        (d.options || []).some((o) => optionText(o).toLowerCase().includes(q))
    );
  }, [dropdowns, listSearch]);

  const categories = useMemo(
    () =>
      Array.from(
        new Set(selectedOptions.map((o) => (o && typeof o === "object" ? o.category : "")).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [selectedOptions]
  );

  const allCategories = useMemo(
    () =>
      Array.from(
        new Set(
          dropdowns.flatMap((d) => (d.options || []).map((o) => (o && typeof o === "object" ? o.category : "")))
        )
      )
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [dropdowns]
  );

  // options with their real index, filtered and grouped for display
  const groupedOptions = useMemo(() => {
    const q = optionSearch.trim().toLowerCase();
    const rows = selectedOptions
      .map((opt, index) => ({ opt, index }))
      .filter(({ opt }) => {
        const cat = opt && typeof opt === "object" ? opt.category || "" : "";
        if (categoryFilter === "__none__" && cat) return false;
        if (categoryFilter && categoryFilter !== "__none__" && cat !== categoryFilter) return false;
        if (!q) return true;
        return (
          optionText(opt).toLowerCase().includes(q) ||
          String(opt?.value || "").toLowerCase().includes(q) ||
          cat.toLowerCase().includes(q)
        );
      });
    if (!hasCategories) return [{ category: "", rows }];
    const groups = [];
    rows.forEach((r) => {
      const cat = (r.opt && r.opt.category) || "Other";
      let g = groups.find((x) => x.category === cat);
      if (!g) {
        g = { category: cat, rows: [] };
        groups.push(g);
      }
      g.rows.push(r);
    });
    return groups.sort((a, b) => (a.category === "Other") - (b.category === "Other") || a.category.localeCompare(b.category));
  }, [selectedOptions, optionSearch, categoryFilter, hasCategories]);

  const visibleOptionCount = groupedOptions.reduce((n, g) => n + g.rows.length, 0);
  const isRequired = (d) => REQUIRED_FIELDS.includes(d?.fieldName);

  // ------------------------------------------------------------------ render
  return (
    <div className="opsui-root opsui-page">
      <div className="opsui-header">
        <div>
          <h1 className="opsui-title">Invoice Dropdowns</h1>
          <p className="opsui-subtitle">
            The pick-lists technicians use on Complete Job &amp; Create Invoice — chemicals, pests, areas treated, weather and more.
          </p>
        </div>
        <div className="opsui-header-actions">
          <button type="button" className="opsui-btn" onClick={() => setTermEditor({ name: "", content: "" })}>
            + Invoice term
          </button>
          <button type="button" className="opsui-btn is-primary" onClick={() => setNewDropdown({ label: "", fieldName: "", touched: false })}>
            + New dropdown
          </button>
        </div>
      </div>

      <div className="opsui-split">
        {/* ---------------- dropdown list ---------------- */}
        <div className="opsui-card" style={{ marginBottom: 0 }}>
          <div className="opsui-card-head">
            <h3 className="opsui-card-title">Dropdowns</h3>
            <span className="opsui-count">{dropdowns.length}</span>
          </div>
          <div style={{ padding: "10px 10px 0" }}>
            <input
              className="opsui-input"
              type="search"
              placeholder="Search dropdowns or options…"
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
            />
          </div>
          {loading ? (
            <EmptyState title="Loading…" />
          ) : visibleDropdowns.length === 0 ? (
            <EmptyState title="No matches" />
          ) : (
            <ul className="opsui-navlist">
              {visibleDropdowns.map((d) => {
                const idx = dropdowns.findIndex((x) => x.id === d.id);
                return (
                  <li
                    key={d.id}
                    className={`opsui-navitem${d.id === selectedId ? " is-active" : ""}`}
                    onClick={() => setSelectedId(d.id)}
                  >
                    {!listSearch && (
                      <div className="opsui-reorder" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="opsui-btn is-ghost"
                          onClick={() => moveDropdown(d.id, -1)}
                          disabled={idx === 0 || busy}
                          title="Move up"
                          aria-label="Move up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="opsui-btn is-ghost"
                          onClick={() => moveDropdown(d.id, 1)}
                          disabled={idx === dropdowns.length - 1 || busy}
                          title="Move down"
                          aria-label="Move down"
                        >
                          ▼
                        </button>
                      </div>
                    )}
                    <div className="opsui-navitem-main">
                      <div className="opsui-navitem-title">{d.label || d.fieldName}</div>
                      <div className="opsui-navitem-sub">{d.fieldName}</div>
                    </div>
                    <Pill tone={(d.options || []).length ? "info" : "neutral"} plain>
                      {(d.options || []).length}
                    </Pill>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ---------------- selected dropdown ---------------- */}
        <div className="opsui-card" style={{ marginBottom: 0 }}>
          {!selected ? (
            <EmptyState title={loading ? "Loading…" : "Select a dropdown"}>
              {!loading && "Choose a dropdown on the left to manage its options."}
            </EmptyState>
          ) : (
            <>
              <div className="opsui-card-head">
                <div style={{ minWidth: 0 }}>
                  <h3 className="opsui-card-title" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    {selected.label || selected.fieldName}
                    <span className="opsui-code">{selected.fieldName}</span>
                    {isRequired(selected) ? (
                      <Pill tone="success">On invoice form</Pill>
                    ) : (
                      <Pill tone="neutral" title="Only the built-in fields appear on the Complete Job & Create Invoice form">
                        Custom
                      </Pill>
                    )}
                  </h3>
                  <p className="opsui-card-sub">
                    {FIELD_HELP[selected.fieldName] ||
                      "Custom dropdown. It is saved, but only the built-in fields appear on the invoice form."}
                  </p>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="opsui-btn is-sm"
                    onClick={() => setEditDropdown({ id: selected.id, label: selected.label || "", fieldName: selected.fieldName || "" })}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className="opsui-btn is-danger is-sm"
                    onClick={() => setConfirmDelete(selected)}
                    disabled={isRequired(selected)}
                    title={isRequired(selected) ? "Built-in dropdowns can’t be deleted — they feed the invoice form" : "Delete dropdown"}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="opsui-toolbar">
                <input
                  className="opsui-input"
                  type="search"
                  placeholder={`Search ${selectedOptions.length} options…`}
                  value={optionSearch}
                  onChange={(e) => setOptionSearch(e.target.value)}
                />
                {(categories.length > 0 || hasCategories) && (
                  <select className="opsui-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                    <option value="">All categories</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                    <option value="__none__">No category</option>
                  </select>
                )}
                <span className="opsui-toolbar-spacer" />
                <button type="button" className="opsui-btn is-sm" onClick={sortOptionsAZ} disabled={busy || selectedOptions.length < 2}>
                  Sort A–Z
                </button>
                <button type="button" className="opsui-btn is-sm" onClick={() => setBulkAdd({ text: "", category: categoryFilter && categoryFilter !== "__none__" ? categoryFilter : "" })}>
                  Bulk add
                </button>
                <button type="button" className="opsui-btn is-primary is-sm" onClick={openAddOption}>
                  + Add option
                </button>
              </div>

              {selectedOptions.length === 0 ? (
                <EmptyState
                  title="No options yet"
                  action={
                    <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                      <button type="button" className="opsui-btn is-primary" onClick={openAddOption}>
                        + Add option
                      </button>
                      <button type="button" className="opsui-btn" onClick={() => setBulkAdd({ text: "", category: "" })}>
                        Paste a list
                      </button>
                    </div>
                  }
                >
                  Technicians will see these choices on the invoice form.
                </EmptyState>
              ) : visibleOptionCount === 0 ? (
                <EmptyState title="No options match" />
              ) : (
                <div>
                  {groupedOptions.map((group) => (
                    <div key={group.category || "flat"}>
                      {hasCategories && group.rows.length > 0 && (
                        <div className="opsui-group-head">
                          {group.category}
                          <span className="opsui-tab-count" style={{ marginLeft: 0 }}>{group.rows.length}</span>
                        </div>
                      )}
                      {group.rows.map(({ opt, index }) => {
                        const title = optionText(opt);
                        const value = opt && typeof opt === "object" ? opt.value : undefined;
                        const subs = opt && typeof opt === "object" && Array.isArray(opt.subOptions) ? opt.subOptions : [];
                        const canReorder = !optionSearch && !categoryFilter;
                        return (
                          <div key={index} className="opsui-option-row">
                            {canReorder && (
                              <div className="opsui-reorder">
                                <button
                                  type="button"
                                  className="opsui-btn is-ghost"
                                  onClick={() => moveOption(index, -1)}
                                  disabled={busy}
                                  title="Move up"
                                  aria-label="Move up"
                                >
                                  ▲
                                </button>
                                <button
                                  type="button"
                                  className="opsui-btn is-ghost"
                                  onClick={() => moveOption(index, 1)}
                                  disabled={busy}
                                  title="Move down"
                                  aria-label="Move down"
                                >
                                  ▼
                                </button>
                              </div>
                            )}
                            <div className="opsui-option-main">
                              <div className="opsui-option-title">{title || <span className="opsui-muted">(blank)</span>}</div>
                              {value !== undefined && String(value) !== title && (
                                <div className="opsui-option-value">{String(value)}</div>
                              )}
                              {subs.length > 0 && (
                                <div className="opsui-option-value">
                                  {subs.length} sub-option{subs.length === 1 ? "" : "s"}
                                </div>
                              )}
                            </div>
                            <div className="opsui-option-actions">
                              <button type="button" className="opsui-btn is-sm" onClick={() => openEditOption(index)}>
                                Edit
                              </button>
                              <button type="button" className="opsui-btn is-danger is-sm" onClick={() => setOptionToDelete(index)}>
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {selectedOptions.length > 0 && (
                <div className="opsui-card-body" style={{ borderTop: "1px solid #e2e8f0" }}>
                  <button type="button" className="opsui-btn is-ghost is-sm" onClick={() => setShowAdvanced((v) => !v)}>
                    {showAdvanced ? "▾" : "▸"} Advanced: add a sub-option to every option
                  </button>
                  {showAdvanced && (
                    <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                      <textarea
                        className="opsui-textarea"
                        rows={3}
                        placeholder="One sub-option paragraph added to ALL options in this dropdown (multi-line allowed)"
                        value={bulkSubOptionText}
                        onChange={(e) => setBulkSubOptionText(e.target.value)}
                      />
                      <div>
                        <button
                          type="button"
                          className="opsui-btn is-sm"
                          onClick={addBulkSubOptionParagraph}
                          disabled={busy || !bulkSubOptionText.trim()}
                        >
                          Add sub-option to all {selectedOptions.length}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ---------------- invoice terms ---------------- */}
      <div className="opsui-card" style={{ marginTop: 16 }}>
        <div className="opsui-card-head">
          <div>
            <h3 className="opsui-card-title">Invoice Terms</h3>
            <p className="opsui-card-sub">Reusable terms & conditions blocks for invoices.</p>
          </div>
          <button type="button" className="opsui-btn is-sm" onClick={() => setTermEditor({ name: "", content: "" })}>
            + Add term
          </button>
        </div>
        {terms.length === 0 ? (
          <EmptyState title="No terms added yet" />
        ) : (
          <div className="opsui-card-body">
            <ul className="opsui-list">
              {terms.map((t) => (
                <li key={t.id} className="opsui-list-item">
                  <div className="opsui-list-item-body">
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{t.name}</div>
                    <div style={{ fontSize: 13.5, whiteSpace: "pre-wrap", color: "#334155" }}>{t.content}</div>
                  </div>
                  <div className="opsui-option-actions">
                    <button type="button" className="opsui-btn is-sm" onClick={() => setTermEditor({ id: t.id, name: t.name || "", content: t.content || "" })}>
                      Edit
                    </button>
                    <button type="button" className="opsui-btn is-danger is-sm" onClick={() => setTermToDelete(t)}>
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ================= modals ================= */}
      {newDropdown && (
        <OpsModal
          title="New dropdown"
          onClose={() => setNewDropdown(null)}
          width={520}
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setNewDropdown(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={addDropdown} disabled={busy}>
                {busy ? "Saving…" : "Create dropdown"}
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 14 }}>
            <Field label="Label" required hint="What technicians see, e.g. “Service Type”">
              <input
                className="opsui-input"
                value={newDropdown.label}
                onChange={(e) => {
                  const label = e.target.value;
                  setNewDropdown((p) => ({ ...p, label, fieldName: p.touched ? p.fieldName : camelCase(label) }));
                }}
                autoFocus
              />
            </Field>
            <Field label="Field name" required hint="Internal key saved on invoices, e.g. serviceType">
              <input
                className="opsui-input"
                value={newDropdown.fieldName}
                onChange={(e) => setNewDropdown((p) => ({ ...p, fieldName: e.target.value.replace(/\s+/g, ""), touched: true }))}
              />
            </Field>
            <div className="opsui-callout is-warn">
              Only the built-in dropdowns ({REQUIRED_FIELDS.join(", ")}) currently appear on the Complete Job &amp; Create
              Invoice form.
            </div>
          </div>
        </OpsModal>
      )}

      {editDropdown && (
        <OpsModal
          title="Rename dropdown"
          onClose={() => setEditDropdown(null)}
          width={480}
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setEditDropdown(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={saveEditedDropdown} disabled={busy}>
                {busy ? "Saving…" : "Save"}
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 14 }}>
            <Field label="Label" required>
              <input
                className="opsui-input"
                value={editDropdown.label}
                onChange={(e) => setEditDropdown((p) => ({ ...p, label: e.target.value }))}
              />
            </Field>
            <Field
              label="Field name"
              required
              hint={
                REQUIRED_FIELDS.includes(dropdowns.find((d) => d.id === editDropdown.id)?.fieldName)
                  ? "Built-in field — locked because the invoice form looks it up by this name."
                  : "Changing this does not rename selections already saved on invoices."
              }
            >
              <input
                className="opsui-input"
                value={editDropdown.fieldName}
                readOnly={REQUIRED_FIELDS.includes(dropdowns.find((d) => d.id === editDropdown.id)?.fieldName)}
                onChange={(e) => setEditDropdown((p) => ({ ...p, fieldName: e.target.value.replace(/\s+/g, "") }))}
              />
            </Field>
          </div>
        </OpsModal>
      )}

      {optionEditor && selected && (
        <OpsModal
          title={optionEditor.mode === "add" ? `Add option to ${selected.label}` : "Edit option"}
          onClose={() => setOptionEditor(null)}
          width={600}
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setOptionEditor(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={saveOption} disabled={busy}>
                {busy ? "Saving…" : optionEditor.mode === "add" ? "Add option" : "Save option"}
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 14 }}>
            <Field label="Option title" required hint="Shown in the dropdown list and added to the job description">
              <input
                className="opsui-input"
                value={optionEditor.data.title}
                onChange={(e) => setOptionEditor((p) => ({ ...p, data: { ...p.data, title: e.target.value } }))}
                autoFocus
              />
            </Field>
            <Field label="Category" hint="Optional — options are grouped under their category on the invoice form">
              <input
                className="opsui-input"
                list="opsui-idd-categories"
                value={optionEditor.data.category}
                onChange={(e) => setOptionEditor((p) => ({ ...p, data: { ...p.data, category: e.target.value } }))}
              />
              <datalist id="opsui-idd-categories">
                {allCategories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </Field>
            <Field label="Paragraph / value" hint="Optional longer text stored with the option. Leave blank to use the title.">
              <textarea
                className="opsui-textarea"
                rows={3}
                value={optionEditor.data.value}
                onChange={(e) => setOptionEditor((p) => ({ ...p, data: { ...p.data, value: e.target.value } }))}
              />
            </Field>
            <Field label={`Sub-options (${optionEditor.data.subOptions.length})`}>
              {optionEditor.data.subOptions.length > 0 && (
                <ul className="opsui-list" style={{ marginBottom: 8 }}>
                  {optionEditor.data.subOptions.map((sub, i) => (
                    <li key={i} className="opsui-list-item" style={{ padding: "6px 10px" }}>
                      <div className="opsui-list-item-body" style={{ fontSize: 13.5, whiteSpace: "pre-wrap" }}>
                        {sub}
                      </div>
                      <button
                        type="button"
                        className="opsui-btn is-ghost is-icon"
                        title="Remove"
                        onClick={() =>
                          setOptionEditor((p) => ({
                            ...p,
                            data: { ...p.data, subOptions: p.data.subOptions.filter((_, j) => j !== i) },
                          }))
                        }
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  className="opsui-input"
                  placeholder="Add a sub-option…"
                  value={optionEditor.newSub}
                  onChange={(e) => setOptionEditor((p) => ({ ...p, newSub: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && optionEditor.newSub.trim()) {
                      e.preventDefault();
                      setOptionEditor((p) => ({ ...p, newSub: "", data: { ...p.data, subOptions: [...p.data.subOptions, p.newSub.trim()] } }));
                    }
                  }}
                />
                <button
                  type="button"
                  className="opsui-btn is-sm"
                  disabled={!optionEditor.newSub.trim()}
                  onClick={() =>
                    setOptionEditor((p) => ({ ...p, newSub: "", data: { ...p.data, subOptions: [...p.data.subOptions, p.newSub.trim()] } }))
                  }
                >
                  Add
                </button>
              </div>
            </Field>
          </div>
        </OpsModal>
      )}

      {bulkAdd && selected && (
        <OpsModal
          title={`Bulk add to ${selected.label}`}
          subtitle="Paste a list — one option per line, or separated by semicolons. Duplicates are skipped."
          onClose={() => setBulkAdd(null)}
          width={560}
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setBulkAdd(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={addBulkOptions} disabled={busy}>
                {busy ? "Saving…" : "Add options"}
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 14 }}>
            <Field label="Options">
              <textarea
                className="opsui-textarea"
                rows={8}
                placeholder={"Ants; Spiders; Roaches\nor one per line"}
                value={bulkAdd.text}
                onChange={(e) => setBulkAdd((p) => ({ ...p, text: e.target.value }))}
                autoFocus
              />
            </Field>
            <Field label="Category for these options" hint="Optional">
              <input
                className="opsui-input"
                list="opsui-idd-categories-bulk"
                value={bulkAdd.category}
                onChange={(e) => setBulkAdd((p) => ({ ...p, category: e.target.value }))}
              />
              <datalist id="opsui-idd-categories-bulk">
                {allCategories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </Field>
            <div className="opsui-hint">
              {bulkAdd.text.split(/[;\n]/).map((s) => s.trim()).filter(Boolean).length} option(s) detected
            </div>
          </div>
        </OpsModal>
      )}

      {termEditor && (
        <OpsModal
          title={termEditor.id ? "Edit invoice term" : "Add invoice term"}
          onClose={() => setTermEditor(null)}
          width={620}
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setTermEditor(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={saveTerm} disabled={busy}>
                {busy ? "Saving…" : "Save term"}
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 14 }}>
            <Field label="Term name" required>
              <input className="opsui-input" value={termEditor.name} onChange={(e) => setTermEditor((p) => ({ ...p, name: e.target.value }))} />
            </Field>
            <Field label="Term content">
              <textarea
                className="opsui-textarea"
                rows={8}
                value={termEditor.content}
                onChange={(e) => setTermEditor((p) => ({ ...p, content: e.target.value }))}
              />
            </Field>
          </div>
        </OpsModal>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={`Delete “${confirmDelete.label}”?`}
          message={`This removes the dropdown and its ${(confirmDelete.options || []).length} option(s).`}
          onConfirm={deleteDropdown}
          onCancel={() => setConfirmDelete(null)}
          busy={busy}
        />
      )}

      {optionToDelete !== null && selected && (
        <ConfirmDialog
          title="Delete option?"
          message={
            <>
              Remove <strong>{optionText(selectedOptions[optionToDelete])}</strong> from {selected.label}? Invoices already
              created keep their saved text.
            </>
          }
          onConfirm={deleteOption}
          onCancel={() => setOptionToDelete(null)}
          busy={busy}
        />
      )}

      {termToDelete && (
        <ConfirmDialog
          title="Delete term?"
          message={<>Delete <strong>{termToDelete.name}</strong>?</>}
          onConfirm={deleteTerm}
          onCancel={() => setTermToDelete(null)}
          busy={busy}
        />
      )}
    </div>
  );
};

export default InvoiceDropdownsAdmin;
